import React, { useMemo, useEffect, useRef } from "react";
import { MessageAnnotations } from "@src/features/chat/MessageAnnotations";
import { useMessageAnnotationsData } from "@src/features/aiAssist/hooks/useMessageAnnotations";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { z } from "zod";
import { AiChatAvatar } from "./AiChatAvatar";
import { splitMessageIntoCanvasParts } from "./utils/canvas";
import { renderMarkdown } from "../chat/renderTextWithInlineCitations";
import { CustomMessage, useChatContext } from "./ChatRuntimeProvider";
import CanvasWidget from "@src/features/aiAssist/uiComponents/ChatCanvasWidget";
import { AskOpusFeedback } from "./AskOpusFeedback";
import Text from "@src/lib/ui/Text";
import ChatBubble from "@src/features/aiAssist/uiComponents/ChatBubble";
import { marked } from "marked";
import ChatAttachment, {
  ChatAttachmentItem,
} from "@src/features/aiAssist/uiComponents/ChatAttachment";
import { Button } from "@src/lib/ui/Button";

// Annotation schemas for validation
const FileCitationMetadataSchema = z
  .object({
    library_item_id: z.string().uuid().nullable().optional(),
    article_id: z.string().nullable().optional(),
    source: z.string().optional(),
    search_score: z.number().optional(),
    content_length: z.number().optional(),
  })
  .optional();

export const FileCitationSchema = z.object({
  type: z.literal("file_citation"),
  file_id: z.union([z.string(), z.number()]), // pgvector sends chunk_id as number
  filename: z.string(),
  quote: z.string().optional(),
  index: z.number().nullable().optional(),
  metadata: FileCitationMetadataSchema,
});

export const IntercomCitationSchema = z.object({
  type: z.literal("intercom_article"),
  file_id: z.union([z.string(), z.number()]),
  filename: z.string(),
  quote: z.string().optional(),
  index: z.number().nullable().optional(),
  metadata: z.object({
    url: z.string().url(),
    title: z.string().optional(),
    description: z.string().optional(),
    article_id: z.string().nullable().optional(),
    library_item_id: z.string().uuid().nullable().optional(),
    source: z.string().optional(),
    search_score: z.number().optional(),
    content_length: z.number().optional(),
  }),
});

export const AnnotationSchema = z.discriminatedUnion("type", [
  FileCitationSchema,
  IntercomCitationSchema,
]);

interface ThreadMessageProps {
  message: CustomMessage;
  messageIndex: number;
  isLastMessage: boolean;
  onSelectCanvas?: (canvasContent: string) => void;
  threadActionButtonHandlers?: {
    [key: string]: () => void;
  };
  chatAvatarName?: string;
}

export const ThreadMessage: React.FC<ThreadMessageProps> = ({
  message,
  messageIndex,
  isLastMessage,
  onSelectCanvas,
  threadActionButtonHandlers,
  chatAvatarName,
}) => {
  const isUser = message.role === "user";
  const { status, chatType } = useChatContext();
  const {
    setArtifact,
    setArtifactLoading,
    setArtifactMessageId,
    selectedSession,
  } = useChatSidebar(chatType);
  const prevStreamingRef = useRef<boolean>(undefined);
  // Convert custom attachments to ChatAttachmentItem format
  const userAttachments = useMemo((): ChatAttachmentItem[] => {
    if (!isUser || !message.customMessageData?.attachments) {
      return [];
    }

    return message.customMessageData.attachments
      .filter(
        (attachment) =>
          attachment.libraryItemFragment || attachment.mediaFragment,
      )
      .map((attachment) => ({
        libraryItemFragment: attachment.libraryItemFragment,
        mediaFragment: attachment.mediaFragment,
      }));
  }, [isUser, message.customMessageData?.attachments]);

  // Shared annotation + references data
  const { referencesLoading, libraryItemsByLookupId } =
    useMessageAnnotationsData(
      Array.isArray(message.annotations) ? message.annotations : [],
    );

  const avatarStatus = useMemo(() => {
    if (!isLastMessage) return "ready";

    const hasActiveFileSearch = message.parts?.some(
      (part) =>
        part.type === "tool-invocation" &&
        part.toolInvocation.toolName === "SearchKnowledgeBase" &&
        part.toolInvocation.state !== "result",
    );

    const hasActiveTeamDataLookup = message.parts?.some(
      (part) =>
        part.type === "tool-invocation" &&
        part.toolInvocation.toolName === "GetTeamData" &&
        part.toolInvocation.state !== "result",
    );

    // Only show tool statuses if the stream is still active AND the tool call is
    if (hasActiveFileSearch && status === "streaming") return "searching";
    if (hasActiveTeamDataLookup && status === "streaming")
      return "checking_data";
    if (status === "streaming") return "streaming";

    return status === "error" ? "ready" : status;
  }, [isLastMessage, message.parts, status]);

  // Handle canvas loading state and artifact setting
  useEffect(() => {
    if (message.role === "assistant") {
      const messageParts = splitMessageIntoCanvasParts(message.content);

      if (messageParts.canvasPart) {
        const isCurrentlyStreaming = messageParts.canvasIsStreaming;
        const wasStreaming = prevStreamingRef.current;

        // Only update loading state when it actually changes
        if (wasStreaming !== isCurrentlyStreaming) {
          setArtifactLoading(isCurrentlyStreaming);
        }

        // If we just finished streaming (was streaming, now not streaming), set the artifact
        if (wasStreaming && !isCurrentlyStreaming) {
          const html = marked.parse(messageParts.canvasPart);
          if (typeof html === "string") {
            setArtifact(html);
            setArtifactMessageId(message.id);
          }
        }

        prevStreamingRef.current = isCurrentlyStreaming;
      }
    }
  }, [
    message.content,
    message.role,
    message.id,
    setArtifact,
    setArtifactLoading,
    setArtifactMessageId,
  ]);

  const messageParts = splitMessageIntoCanvasParts(message.content);
  const handleButtonClick = () => {
    if (threadActionButtonHandlers?.[messageParts.buttonPart]) {
      threadActionButtonHandlers[messageParts.buttonPart]();
    }
  };

  if (isUser) {
    const showLoadingAvatar = isLastMessage && status === "submitted";

    return (
      <>
        {userAttachments.length > 0 && (
          <div className="mb-1 flex w-full max-w-2xl flex-wrap justify-end gap-1">
            {userAttachments.map((attachment, index) => (
              <ChatAttachment key={index} {...attachment} />
            ))}
          </div>
        )}
        <ChatBubble content={message.content} />
        {showLoadingAvatar && (
          <Text variant="compact-description" multiline className="my-2 w-full">
            <div className="mb-2">
              <AiChatAvatar status="submitted" name={chatAvatarName} />
            </div>
          </Text>
        )}
      </>
    );
  }

  return (
    <>
      <div data-testid={`ai-message-${messageIndex}`} className="my-2 w-full">
        <Text variant="compact-description" multiline>
          <div className="mb-2">
            <AiChatAvatar status={avatarStatus} name={chatAvatarName} />
          </div>
          <div
            className="break-words"
            data-testid={`ai-message-content-${messageIndex}`}
          >
            {messageParts.introPart && (
              <div>{renderMarkdown(messageParts.introPart)}</div>
            )}
            {messageParts.canvasPart && (
              <div className="py-2">
                <CanvasWidget
                  isStreaming={
                    messageParts.canvasIsStreaming &&
                    status === "streaming" &&
                    isLastMessage
                  }
                  onClick={() =>
                    messageParts.canvasPart &&
                    onSelectCanvas?.(messageParts.canvasPart)
                  }
                  canvasContent={renderMarkdown(messageParts.canvasPart)}
                  isViewing={
                    !!selectedSession?.artifact &&
                    selectedSession.artifact.replace(/<[^>]*>/g, "").trim() !==
                      "" &&
                    selectedSession?.artifactMessageId === message.id
                  }
                />
              </div>
            )}
            {messageParts.outroPart && (
              <div>{renderMarkdown(messageParts.outroPart)}</div>
            )}
          </div>
        </Text>
      </div>
      {message.annotations &&
        Array.isArray(message.annotations) &&
        message.annotations.length > 0 &&
        message.role === "assistant" &&
        (!isLastMessage || (isLastMessage && status === "ready")) && (
          <div className="w-full self-stretch">
            <MessageAnnotations
              message={message}
              messageIndex={messageIndex}
              referencesLoading={referencesLoading}
              libraryItemsByLookupId={libraryItemsByLookupId}
            />
          </div>
        )}
      <div className="self-start">
        {isLastMessage &&
          messageParts.buttonPart &&
          // When a consumer provides a handler map, only render the
          // agent-suggested action button if THIS button has a handler. That
          // lets a consumer suppress a suggested action by omitting its handler
          // (e.g. the module outline builder hides "Add Details" when
          // product-container generation is on, leaving its handler map empty).
          // Consumers that pass no handler map keep the legacy behavior
          // (render the button regardless), so other chat flows are unaffected.
          (threadActionButtonHandlers === undefined ||
            threadActionButtonHandlers[messageParts.buttonPart]) && (
            <Button
              className="mt-2"
              onClick={handleButtonClick}
              disabled={
                selectedSession?.artifactLoading || status === "streaming"
              }
            >
              {messageParts.buttonPart}
            </Button>
          )}
        {message.role === "assistant" &&
          message?.id &&
          !(isLastMessage && status === "streaming") && (
            <AskOpusFeedback vercelChatMessageId={message.id} />
          )}
      </div>
    </>
  );
};
